import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  BarChart3,
  Bell,
  Brain,
  Building2,
  Calendar,
  DollarSign,
  Heart,
  Home,
  LogOut,
  Menu,
  Moon,
  ScrollText,
  Search,
  Settings,
  Sun,
  TrendingUp,
  User,
  Users,
  Waves,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: string[];
}

const allNavItems: NavItem[] = [
  { href: "/dashboard", label: "Dashboard", icon: Home },
  {
    href: "/agenda",
    label: "Agenda",
    icon: Calendar,
    roles: ["ADMIN", "PSICOLOGA", "RECEPCAO"],
  },
  {
    href: "/pacientes",
    label: "Pacientes",
    icon: Users,
    roles: ["ADMIN", "PSICOLOGA", "RECEPCAO"],
  },
  {
    href: "/escalas",
    label: "Escalas",
    icon: Brain,
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    href: "/financeiro",
    label: "Financeiro",
    icon: DollarSign,
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    href: "/nr1",
    label: "NR-1",
    icon: BarChart3,
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    href: "/contratos",
    label: "Contratos",
    icon: ScrollText,
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    href: "/produtividade",
    label: "Produtividade",
    icon: TrendingUp,
    roles: ["ADMIN", "PSICOLOGA"],
  },
  {
    href: "/notificacoes",
    label: "Notificações",
    icon: Bell,
    roles: ["ADMIN", "PSICOLOGA", "RECEPCAO"],
  },
  {
    href: "/meu-espaco",
    label: "Meu Espaço",
    icon: Heart,
    roles: ["PACIENTE", "FAMILIAR"],
  },
];

const adminNavItems: NavItem[] = [
  {
    href: "/admin/usuarios",
    label: "Usuários",
    icon: Settings,
    roles: ["ADMIN"],
  },
  {
    href: "/admin/empresas",
    label: "Empresas",
    icon: Building2,
    roles: ["ADMIN", "PSICOLOGA"],
  },
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const userRole = user?.role || "";

  const filteredNavItems = useMemo(
    () =>
      allNavItems.filter(
        (item) => !item.roles || item.roles.includes(userRole),
      ),
    [userRole],
  );

  const filteredAdminItems = useMemo(
    () =>
      adminNavItems.filter(
        (item) => !item.roles || item.roles.includes(userRole),
      ),
    [userRole],
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      ADMIN: "Administrador",
      PSICOLOGA: "Psicóloga",
      RECEPCAO: "Recepção",
      PACIENTE: "Paciente",
      FAMILIAR: "Familiar",
      EMPRESA: "Empresa",
    };
    return labels[role] || role;
  };

  const isActive = (href: string) => {
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  };

  return (
    <div
      className={`min-h-screen flex ${theme === "dark" ? "dark bg-[hsl(215,40%,10%)] text-[hsl(39,20%,88%)]" : "bg-brand-off-white"}`}
    >
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-brand-azul-profundo focus:text-white focus:rounded-lg"
      >
        Pular para conteúdo principal
      </a>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 border-r
          ${theme === "dark" ? "bg-[hsl(215,35%,13%)] border-white/10" : "bg-white border-slate-200"}
          transform transition-transform duration-200 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
        {...(isSidebarOpen
          ? {
              role: "dialog",
              "aria-modal": true,
              "aria-label": "Menu de navegação",
            }
          : {})}
        onKeyDown={(e) => {
          if (e.key === "Escape" && isSidebarOpen) {
            setIsSidebarOpen(false);
          }
        }}
      >
        {/* Sidebar Header */}
        <div
          className={`h-16 flex items-center justify-between px-4 border-b ${theme === "dark" ? "border-white/10" : "border-slate-200"}`}
        >
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-azul-profundo text-white">
              <Waves className="h-4 w-4" />
            </div>
            <div>
              <div
                className={`font-serif text-lg leading-none ${theme === "dark" ? "text-white" : "text-brand-azul-profundo"}`}
              >
                maré
              </div>
              <div
                className={`text-[10px] uppercase tracking-[0.2em] ${theme === "dark" ? "text-white/50" : "text-slate-500"}`}
              >
                Instituto
              </div>
            </div>
          </Link>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className={`lg:hidden p-3 min-h-[44px] min-w-[44px] flex items-center justify-center rounded ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-slate-100"}`}
            aria-label="Fechar menu"
          >
            <X
              className={`h-5 w-5 ${theme === "dark" ? "text-white/60" : "text-slate-500"}`}
            />
          </button>
        </div>

        {/* Navigation */}
        <nav
          className="p-4 space-y-1 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 10rem)" }}
        >
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setIsSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium
                  transition-colors duration-150
                  ${
                    active
                      ? "bg-brand-azul-profundo text-white"
                      : theme === "dark"
                        ? "text-white/70 hover:bg-white/10 hover:text-white"
                        : "text-slate-600 hover:bg-slate-100"
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                {item.label}
              </Link>
            );
          })}

          {filteredAdminItems.length > 0 && (
            <>
              <div className="pt-4 pb-2">
                <span
                  className={`px-3 text-xs font-semibold uppercase tracking-wider ${theme === "dark" ? "text-white/40" : "text-slate-400"}`}
                >
                  Administração
                </span>
              </div>
              {filteredAdminItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium
                      transition-colors duration-150
                      ${
                        active
                          ? "bg-brand-azul-profundo text-white"
                          : theme === "dark"
                            ? "text-white/70 hover:bg-white/10 hover:text-white"
                            : "text-slate-600 hover:bg-slate-100"
                      }
                    `}
                  >
                    <Icon className="h-5 w-5" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User Info at Bottom */}
        <div
          className={`absolute bottom-0 left-0 right-0 p-4 border-t ${theme === "dark" ? "border-white/10 bg-[hsl(215,35%,13%)]" : "border-slate-200 bg-white"}`}
        >
          {user && (
            <div className="flex items-center gap-3 mb-3">
              <div
                className={`h-10 w-10 rounded-full flex items-center justify-center ${theme === "dark" ? "bg-white/10" : "bg-brand-azul-profundo/10"}`}
              >
                <User
                  className={`h-5 w-5 ${theme === "dark" ? "text-white/80" : "text-brand-azul-profundo"}`}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium truncate ${theme === "dark" ? "text-white" : "text-slate-900"}`}
                >
                  {user.name}
                </p>
                <p
                  className={`text-xs ${theme === "dark" ? "text-white/50" : "text-slate-500"}`}
                >
                  {getRoleLabel(user.role)}
                </p>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleTheme}
              className={`flex-1 ${theme === "dark" ? "border-white/20 text-white/70 hover:text-white hover:bg-white/10" : "text-slate-600 hover:text-slate-900"}`}
              aria-label={
                theme === "dark" ? "Ativar modo claro" : "Ativar modo escuro"
              }
            >
              {theme === "dark" ? (
                <Sun className="h-4 w-4 mr-2" />
              ) : (
                <Moon className="h-4 w-4 mr-2" />
              )}
              {theme === "dark" ? "Claro" : "Escuro"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className={`flex-1 ${theme === "dark" ? "border-white/20 text-white/70 hover:text-red-400 hover:border-red-400/30" : "text-slate-600 hover:text-red-600 hover:border-red-200"}`}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top Bar */}
        <header
          className={`h-16 border-b flex items-center justify-between px-4 lg:px-6 ${theme === "dark" ? "bg-[hsl(215,35%,13%)] border-white/10" : "bg-white border-slate-200"}`}
        >
          <button
            onClick={() => setIsSidebarOpen(true)}
            className={`lg:hidden p-3 rounded-lg ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-slate-100"}`}
            aria-label="Abrir menu"
          >
            <Menu
              className={`h-5 w-5 ${theme === "dark" ? "text-white/60" : "text-slate-600"}`}
            />
          </button>

          <div className="hidden lg:flex items-center">
            <button
              onClick={() =>
                document.dispatchEvent(
                  new KeyboardEvent("keydown", { key: "k", metaKey: true }),
                )
              }
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                theme === "dark"
                  ? "border-white/20 text-white/50 hover:text-white/70 hover:border-white/30"
                  : "border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300"
              }`}
              aria-label="Abrir busca rápida (Cmd+K)"
            >
              <Search className="h-3.5 w-3.5" />
              <span>Buscar...</span>
              <kbd className="hidden sm:inline-flex h-5 items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                ⌘K
              </kbd>
            </button>
          </div>

          <div className="flex items-center gap-4">
            <NotificationBell />
            {user && (
              <span
                className={`hidden sm:inline-flex items-center text-sm ${theme === "dark" ? "text-white/60" : "text-slate-600"}`}
              >
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${theme === "dark" ? "bg-white/15 text-white/80" : "bg-brand-azul-profundo/15 text-brand-azul-profundo"}`}
                >
                  {getRoleLabel(user.role)}
                </span>
              </span>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main id="main-content" className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
